import { createContext, useContext, useState, ReactNode, useMemo } from 'react';

import type { FilterValueType } from './types';

type FilterContextType = {
  /**
   * Boolean value to signify the visibility of the filter
   */
  isFilterVisible: boolean;
  /**
   * Function to control the visibility of the filter
   */
  setIsFilterVisible: (filterState: boolean) => void;
  /**
   * Object with current applied filters
   */
  appliedFilters: FilterValueType;
  /**
   * Function to update the applied filters
   */
  setAppliedFilters: (filter: FilterValueType) => void;
  /**
   * Number of the applied filters
   */
  numberOfFiltersApplied: number;
  /**
   * Function to reset the filter visibility and set it to initialFilter values
   */
  resetFilter: () => void;
};

const initialFilters: FilterValueType = [];

/**
 * This context component will be the source of truth for the applied filters and setting the filters
 */
const FilterContext = createContext<FilterContextType | undefined>(undefined);

export const FilterProvider = ({ children }: { children: ReactNode }) => {
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [appliedFilters, setAppliedFilters] =
    useState<FilterValueType>(initialFilters);

  // Get the number of applied/selected filters
  const numberOfFiltersApplied = useMemo(
    () => appliedFilters.length,
    [appliedFilters]
  );

  // reset filter to initial values and close it
  const resetFilter = () => {
    setAppliedFilters(initialFilters);
    setIsFilterVisible(false);
  };

  return (
    <FilterContext.Provider
      value={{
        isFilterVisible,
        setIsFilterVisible,
        appliedFilters,
        setAppliedFilters,
        numberOfFiltersApplied,
        resetFilter,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
};

export const useFilter = () => {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useFilter must be used within a FilterProvider');
  }
  return context;
};
